import { useState, useEffect, useRef } from 'react'
import { C, font } from '../design'

function useIsMobile(bp = 640) {
  const [v, setV] = useState(() => window.innerWidth < bp)
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [bp])
  return v
}

const LIKERT_OPTIONS = [
  { value: 1, label: 'Полностью согласен' },
  { value: 2, label: 'Скорее согласен' },
  { value: 3, label: 'Скорее не согласен' },
  { value: 4, label: 'Категорически не согласен' },
]

const ARCHETYPE_LABELS = {
  hero: 'Герой', sage: 'Мудрец', creator: 'Творец', ruler: 'Правитель',
  caregiver: 'Заботливый', explorer: 'Исследователь', rebel: 'Бунтарь',
  magician: 'Маг', lover: 'Влюблённый', jester: 'Шут', innocent: 'Невинный',
  orphan: 'Сирота', extra_e: 'Э/И', extra_i: 'Э/И', extra_n: 'Интуиция', extra_g: 'Планирование',
}

function QuestionCard({ q, index, answer, onChange, isMobile }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: '20px 24px',
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
        <span style={{
          fontFamily: font.mono, fontSize: 11, color: C.textMuted,
          background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
          borderRadius: 4, padding: '3px 8px', whiteSpace: 'nowrap', flexShrink: 0, marginTop: 2,
        }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <p style={{
          fontFamily: font.body, fontSize: 15, color: C.text,
          margin: 0, lineHeight: 1.55, flex: 1,
        }}>
          {q.text}
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 8 }}>
        {LIKERT_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(q.id, opt.value)}
            style={{
              fontFamily: font.body,
              fontSize: 13,
              padding: '10px 14px',
              borderRadius: 8,
              border: `1px solid ${answer === opt.value ? C.accent : C.border}`,
              background: answer === opt.value ? 'rgba(184,189,212,0.14)' : 'rgba(255,255,255,0.02)',
              color: answer === opt.value ? C.text : C.textMuted,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
              lineHeight: 1.3,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const SURVEY_MESSAGES = [
  'Читаем ответы анкеты...',
  'Определяем доминирующие архетипы...',
  'Анализируем ценности и мотивацию...',
  'Строим профиль личности...',
  'Оцениваем компетенции и сильные стороны...',
  'Формируем карьерные рекомендации...',
  'Завершаем профиль...',
]

export function SurveyDoneScreen({ personality, onViewProfile }) {
  const archetype = personality?.archetype || ''
  const mbti = personality?.mbti_type || ''
  const summary = personality?.summary || ''
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
      <style>{`
        @keyframes donePulse { 0%,100%{opacity:.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.12)} }
        @keyframes doneFadeIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
      <div style={{ position: 'relative', width: 72, height: 72, marginBottom: 32 }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid rgba(184,189,212,0.4)', animation: 'donePulse 3s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>◈</div>
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 400, fontSize: 32, color: '#dce0f0', marginBottom: 8, letterSpacing: '0.02em', animation: 'doneFadeIn 0.6s ease both' }}>
        Профиль создан
      </div>
      {(archetype || mbti) && (
        <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, color: C.accent, marginBottom: 16, letterSpacing: '0.08em', animation: 'doneFadeIn 0.6s ease 0.15s both', opacity: 0 }}>
          {[archetype, mbti].filter(Boolean).join(' · ')}
        </div>
      )}
      {summary && (
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 14, color: C.textMuted, maxWidth: 400, lineHeight: 1.6, marginBottom: 40, animation: 'doneFadeIn 0.6s ease 0.3s both', opacity: 0 }}>
          {summary.slice(0, 180)}{summary.length > 180 ? '…' : ''}
        </div>
      )}
      <button
        onClick={onViewProfile}
        style={{
          fontFamily: "'Inter',sans-serif", fontSize: 14,
          padding: '12px 32px', borderRadius: 24,
          border: `1px solid ${C.accent}`,
          background: 'rgba(184,189,212,0.1)',
          color: C.text, cursor: 'pointer',
          letterSpacing: '0.04em',
          animation: 'doneFadeIn 0.6s ease 0.45s both', opacity: 0,
        }}
      >
        Посмотреть профиль →
      </button>
    </div>
  )
}

export function SurveyAnalyzingScreen() {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000)
    return () => clearInterval(t)
  }, [])
  const barPct = Math.min(97, Math.round((elapsed / 110) * 97))
  const msgIdx = Math.min(SURVEY_MESSAGES.length - 1, Math.floor(elapsed / 16))
  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60
  const timeStr = mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs}с`
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
      <style>{`
        @keyframes surveyPulse { 0%,100%{opacity:.4;transform:scale(1)} 50%{opacity:1;transform:scale(1.08)} }
        @keyframes surveyShimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(400%)} }
      `}</style>
      <div style={{ position: 'relative', width: 64, height: 64, marginBottom: 32 }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid rgba(184,189,212,0.3)', animation: 'surveyPulse 2s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', inset: 8, borderRadius: '50%', border: '1px solid rgba(184,189,212,0.15)', animation: 'surveyPulse 2s ease-in-out infinite 0.4s' }} />
        <div style={{ position: 'absolute', inset: '50%', transform: 'translate(-50%,-50%)', width: 8, height: 8, borderRadius: '50%', background: 'rgba(184,189,212,0.7)', animation: 'surveyPulse 2s ease-in-out infinite 0.8s' }} />
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 400, fontSize: 26, color: '#dce0f0', marginBottom: 10, letterSpacing: '0.02em' }}>Создаём профиль</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#8890aa', marginBottom: 32, minHeight: 20 }}>{SURVEY_MESSAGES[msgIdx]}</div>
      <div style={{ width: '100%', maxWidth: 340, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8890aa', fontFamily: "'Space Mono',monospace", marginBottom: 6 }}>
          <span>анализ</span><span>{timeStr}</span>
        </div>
        <div style={{ height: 3, background: 'rgba(180,185,230,0.09)', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
          <div style={{ height: '100%', width: `${barPct}%`, background: 'rgba(184,189,212,0.5)', borderRadius: 2, transition: 'width 1s linear', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, width: '30%', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)', animation: 'surveyShimmer 2s linear infinite' }} />
          </div>
        </div>
      </div>
      <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 10, color: 'rgba(136,144,170,0.5)' }}>обычно 1–2 минуты</div>
    </div>
  )
}

export default function Survey({ questions, groupNum, totalGroups = 2, onSubmit, loading = false }) {
  const [answers, setAnswers] = useState({})
  const topRef = useRef(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    setAnswers({})
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [groupNum])

  const answered = Object.keys(answers).length
  const total = questions.length
  const allDone = answered === total

  const handleChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }))
  }

  const handleSubmit = () => {
    if (!allDone || loading) return
    const responses = questions.map(q => ({
      question_id:   q.id,
      question_text: q.text,
      archetype:     q.archetype,
      is_check:      q.is_check || false,
      answer_value:  answers[q.id],
      answer_label:  LIKERT_OPTIONS.find(o => o.value === answers[q.id])?.label || '',
    }))
    onSubmit(responses)
  }

  return (
    <div ref={topRef} style={{ fontFamily: font.body, color: C.text, padding: '0 0 60px' }}>
      {/* Хедер */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Часть {groupNum} из {totalGroups}
          </span>
          <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted }}>·</span>
          <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted }}>
            {total} утверждений
          </span>
        </div>
        <h2 style={{ fontFamily: font.heading, fontWeight: 400, fontSize: 30, color: C.text, margin: '0 0 8px' }}>
          {groupNum === 1 ? 'Базовые архетипы' : 'Углублённое профилирование'}
        </h2>
        <p style={{ fontSize: 13, color: C.textMuted, margin: 0, lineHeight: 1.5 }}>
          Оцени каждое утверждение насколько оно соответствует тебе. Выбирай первое ощущение — без долгих раздумий.
        </p>
      </div>

      {/* Прогресс */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: C.textMuted }}>
          <span>Отвечено</span>
          <span style={{ color: allDone ? C.green : C.textMuted }}>{answered} / {total}</span>
        </div>
        <div style={{ height: 3, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${Math.round((answered / total) * 100)}%`,
            background: allDone ? C.green : C.accent,
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Вопросы */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
        {questions.map((q, i) => (
          <QuestionCard
            key={q.id}
            q={q}
            index={i}
            answer={answers[q.id] ?? null}
            onChange={handleChange}
            isMobile={isMobile}
          />
        ))}
      </div>

      {/* Кнопка */}
      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button
          onClick={handleSubmit}
          disabled={!allDone || loading}
          style={{
            fontFamily: font.body,
            fontSize: 14,
            padding: '12px 32px',
            borderRadius: 8,
            border: `1px solid ${allDone ? C.accent : C.border}`,
            background: allDone ? 'rgba(184,189,212,0.12)' : 'transparent',
            color: allDone ? C.text : C.textMuted,
            cursor: allDone && !loading ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
          }}
        >
          {loading
            ? groupNum === totalGroups ? '◎ Генерирую профиль...' : '◎ Генерирую вторую часть...'
            : groupNum === totalGroups ? `Завершить анкету` : `Перейти к части ${groupNum + 1} →`
          }
        </button>
        {!allDone && (
          <span style={{ fontSize: 12, color: C.textMuted }}>
            Ответь на все {total - answered} оставшихся
          </span>
        )}
      </div>
    </div>
  )
}
